/**
 * PaymentPage - Clean Shopify-inspired payment selection
 * Route: /order/:sessionId/pay
 */

import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useBrandTheme } from "@/lib/brand-theme";
import { formatCurrency, cn } from "@/lib/utils";
import { analytics, trackPaymentAttempted } from "@/lib/analytics";
import { Card } from "@/components/Card";
import { Button } from "@/components/Button";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { ErrorMessage } from "@/components/ErrorMessage";
import { useOrder } from "@/api/hooks";
import { useTheme } from "@/lib/theme/ThemeProvider";
import { analyticsApi } from "@/api/client";
import Paystack from "@paystack/inline-js";

// Secure checkout badge
function SecureBadge() {
  const { resolvedTheme, setTheme } = useTheme();

  const toggleTheme = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  };

  return (
    <div className="flex items-center gap-3">
      <div className="secure-badge">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        <span>Secure checkout</span>
      </div>

      <button
        onClick={toggleTheme}
        className="p-2 rounded-lg hover:bg-[var(--color-surface-secondary)] transition-colors"
        aria-label={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} mode`}
        title={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} mode`}
      >
        {resolvedTheme === "dark" ? (
          <svg
            className="w-5 h-5 text-[var(--color-text-secondary)]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ) : (
          <svg
            className="w-5 h-5 text-[var(--color-text-secondary)]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
            />
          </svg>
        )}
      </button>
    </div>
  );
}

// Breadcrumb component
function Breadcrumb() {
  return (
    <nav className="checkout-breadcrumb mb-6">
      <span className="checkout-breadcrumb-item completed">Cart</span>
      <svg
        className="w-4 h-4 text-[var(--color-text-placeholder)]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
      <span className="checkout-breadcrumb-item completed">Shipping</span>
      <svg
        className="w-4 h-4 text-[var(--color-text-placeholder)]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
      <span className="checkout-breadcrumb-item completed">Review</span>
      <svg
        className="w-4 h-4 text-[var(--color-text-placeholder)]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
      <span className="checkout-breadcrumb-item active">Payment</span>
    </nav>
  );
}

export function PaymentPage() {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const { theme } = useBrandTheme();
  const { resolvedTheme } = useTheme();

  const [showSummary, setShowSummary] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedShippingMethod, setSelectedShippingMethod] = useState<{
    id: string;
    name: string;
    description: string;
    price_cents: number;
  } | null>(null);
  const [paystackReady, setPaystackReady] = useState(false);

  // Fetch real order data from API
  const { data: orderData, isLoading: orderLoading, error: orderError } = useOrder(orderId);

  // Load shipping method from localStorage
  useEffect(() => {
    const savedShippingMethod = localStorage.getItem("selectedShippingMethod");
    if (savedShippingMethod) {
      try {
        const shippingOption = JSON.parse(savedShippingMethod);
        setSelectedShippingMethod(shippingOption);
      } catch (error) {
        console.error("Failed to parse shipping method from localStorage:", error);
        if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
          Sentry.captureException(error, {
            tags: { component: "PaymentPage", action: "parseShippingMethod" },
          });
        }
      }
    }
  }, []);

  // Calculate totals from order data
  const shippingCents = orderData?.order.shipping_cents || selectedShippingMethod?.price_cents || 0;
  const subtotalCents = orderData?.items.reduce((sum, item) => sum + item.line_total_cents, 0) || 0;
  const totalCents = orderData?.order?.total_cents
    ? orderData?.order?.total_cents + shippingCents
    : subtotalCents + shippingCents;

  // Track page view
  useEffect(() => {
    if (sessionId) {
      analytics.pageView("payment");
    }
  }, [sessionId]);

  // Redirect if no order ID
  useEffect(() => {
    if (!orderId) {
      navigate(`/order/${sessionId}`);
    }
  }, [orderId, sessionId, navigate]);

  // Paystack is now loaded via npm package - always ready
  useEffect(() => {
    setPaystackReady(true);
  }, []);

  const handlePaystackPayment = useCallback(() => {
    if (!orderData) {
      setError("Order data is not available. Please try again.");
      return;
    }

    setIsProcessing(true);
    setError(null);

    trackPaymentAttempted(totalCents, "paystack");

    const publicKey = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY;

    if (!publicKey) {
      setError("Payment configuration is missing. Please contact support.");
      setIsProcessing(false);
      return;
    }

    const paystack = new Paystack();

    // Extract name from shipping info
    const fullName = orderData.order.shipping?.name || "";
    const nameParts = fullName.trim().split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";

    const metadata = {
      order_id: orderId,
      session_id: sessionId,
      shipping_cost: shippingCents,
      shipping_name: selectedShippingMethod?.name || null,
      custom_fields: [
        {
          display_name: "Order ID",
          variable_name: "order_id",
          value: orderId,
        },
        {
          display_name: "Session ID",
          variable_name: "session_id",
          value: sessionId || "",
        },
        {
          display_name: "Shipping Cost",
          variable_name: "shipping_cost",
          value: shippingCents || null,
        },
      ],
    };

    // console.log("Sending to Paystack:", {
    //   key: publicKey,
    //   email: orderData.order.shipping?.email || "customer@example.com",
    //   amount: totalCents,
    //   currency: orderData.order.currency || "ZAR",
    //   reference: `order-${orderId}-${Date.now()}`,
    //   firstName,
    //   lastName,
    //   phone: orderData.order.shipping?.phone || "",
    //   metadata,
    // });

    paystack.newTransaction({
      key: publicKey,
      email: orderData.order.shipping?.email || "customer@example.com",
      amount: totalCents,
      currency: orderData.order.currency || "ZAR",
      reference: `order-${orderId}-${Date.now()}`,
      firstName: firstName,
      lastName: lastName,
      phone: orderData.order.shipping?.phone || "",
      metadata: metadata,
      onLoad: (response: { id: string; accessCode: string; customer: any }) => {
        console.log("Transaction loaded:", response);
      },
      onSuccess: (transaction: {
        reference: string;
        status: string;
        trans?: string;
        trxref?: string;
      }) => {
        analytics.stepCompleted("payment");
        // console.log("Payment successful:", transaction);
        console.log("Payment successful");

        if (sessionId) {
          analyticsApi
            .track({
              session_id: sessionId,
              event: "order_completed",
              properties: {
                retailer_id: orderData.order.retailer_id,
                session_id: sessionId,
                order_id: orderId,
                total: totalCents,
                creator_id: orderData.order.customer_id || null,
              },
            })
            .catch((error) => {
              console.error("Failed to track order_completed:", error);
            });
        }

        // if (orderId) {
        //   updateOrderAfterPayment(orderId, transaction.reference)
        //     .then(() => {
        //       console.log("Order and Cart updated successfully");
        //     })
        //     .catch((error) => {
        //       console.error("Failed to update order after payment:", error);
        //     });
        // }

        navigate(
          `/order/${sessionId}/success?order_id=${orderId}&reference=${transaction.reference}`
        );
      },
      onCancel: () => {
        console.log("Payment cancelled by user");
        setIsProcessing(false);
      },
      onError: (error: { message: string }) => {
        console.error("Payment error:", error);
        setError(error.message || "Payment failed. Please try again.");
        setIsProcessing(false);
      },
    });
  }, [orderData, orderId, sessionId, navigate, totalCents]);

  const handleCancel = () => {
    navigate(`/order/${sessionId}`);
  };

  // Loading state
  if (orderLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-primary)]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Error state
  if (orderError || !orderData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#f6f6f7]">
        <ErrorMessage
          title="Failed to load order"
          message={orderError instanceof Error ? orderError.message : "Order not found"}
          className="max-w-md"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-primary)] flex flex-col justify-between gap-5">
      {/* Header */}
      <div>
        <header className="bg-[var(--color-surface-primary)] border-b border-[var(--color-surface-border-light)]">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              {theme?.logo_url ? (
                <img src={theme.logo_url} alt={theme.name} className="h-8 w-auto" />
              ) : (
                <div className="text-[18px] font-semibold text-[var(--color-text-primary)]">
                  {theme?.name || "Checkout"}
                </div>
              )}
              <SecureBadge />
            </div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 py-8">
          <Breadcrumb />

          <div className="lg:grid lg:grid-cols-[1fr_380px] lg:gap-4">
            {/* Main content */}
            <div>
              <Card className="mb-6">
                <h2 className="text-[16px] font-semibold text-[var(--color-text-primary)] mb-4">
                  Payment method
                </h2>
                <p className="text-[14px] text-[var(--color-text-secondary)] mb-4">
                  All transactions are secure and encrypted.
                </p>

                <div className="flex items-center gap-3 p-4 border border-[var(--color-surface-border-light)] rounded-lg bg-[var(--color-bg-secondary)]">
                  <div className="w-10 h-6 bg-[#00C3F7] rounded flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-[10px] font-bold">PS</span>
                  </div>
                  <div className="flex-1">
                    <span className="text-[14px] font-medium text-[var(--color-text-primary)]">
                      Paystack
                    </span>
                    <p className="text-[12px] text-[var(--color-text-muted)]">
                      Card, Bank Transfer, USSD, Mobile Money
                    </p>
                  </div>
                </div>
              </Card>

              {error && (
                <div className="error-box mb-6">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <div>
                    <p className="font-medium">Payment Error</p>
                    <p className="text-[12px] opacity-80 mt-0.5">{error}</p>
                  </div>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleCancel}
                  variant="accent"
                  disabled={isProcessing}
                  className="opacity-100"
                >
                  Return to Review
                </Button>
                <Button
                  onClick={handlePaystackPayment}
                  disabled={isProcessing || !paystackReady}
                  isLoading={isProcessing}
                  fullWidth
                  size="lg"
                >
                  {isProcessing
                    ? "Processing..."
                    : !paystackReady
                      ? "Loading Payment Provider..."
                      : formatCurrency(totalCents)}
                </Button>
              </div>

              {/* Security Notice */}
              <div className="mt-6 flex items-center justify-center gap-2 text-[12px] text-[var(--color-text-muted)]">
                <svg
                  className="w-4 h-4 text-[var(--color-success)]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
                Your payment info is secure and encrypted
              </div>
            </div>

            {/* Order summary sidebar */}
            <div className="hidden lg:block">
              <div className="sticky top-8">
                <Card>
                  <h2 className="text-[16px] font-semibold text-[var(--color-text-primary)] mb-4">
                    Order summary
                  </h2>

                  {/* Products */}
                  <div className="border-b border-[var(--color-surface-border-light)] pb-4 mb-4">
                    {orderData.items.map((item) => (
                      <div key={item.id} className="flex items-center gap-3 py-2">
                        <div className="relative">
                          <div className="w-12 h-12 bg-[var(--color-bg-secondary)] rounded border border-[var(--color-surface-border-light)] flex items-center justify-center overflow-hidden">
                            {item.image_url ? (
                              <img
                                src={item.image_url}
                                alt={item.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <svg
                                className="w-6 h-6 text-[var(--color-text-placeholder)]"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={1.5}
                                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                              </svg>
                            )}
                          </div>
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[var(--color-text-muted)] text-white text-[10px] font-medium rounded-full flex items-center justify-center">
                            {item.quantity}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium text-[var(--color-text-primary)] truncate">
                            {item.title}
                          </p>
                          <p className="truncate text-[12px] text-[var(--color-text-muted)] mt-0.5">
                            Quantity: {item.quantity}
                          </p>
                        </div>
                        <span className="text-[13px] font-medium text-[var(--color-text-primary)] tabular-nums">
                          {formatCurrency(item.line_total_cents)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Totals */}
                  <div className="space-y-2 text-[14px]">
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-secondary)]">Subtotal</span>
                      <span className="text-[var(--color-text-primary)] font-medium tabular-nums">
                        {formatCurrency(subtotalCents)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-secondary)]">Shipping</span>
                      <span className="text-[var(--color-text-primary)] font-medium tabular-nums">
                        {shippingCents > 0 ? formatCurrency(shippingCents) : "—"}
                      </span>
                    </div>
                    {selectedShippingMethod && shippingCents > 0 && (
                      <p className="text-[12px] text-[var(--color-text-muted)] -mt-1">
                        {selectedShippingMethod.name}
                      </p>
                    )}
                    <div className="divider" />
                    <div className="flex justify-between items-baseline">
                      <span className="text-[var(--color-text-primary)] font-semibold">Total</span>
                      <span className="price-total tabular-nums">{formatCurrency(totalCents)}</span>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Mobile order summary toggle */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-[var(--color-surface-primary)] border-t border-[var(--color-surface-border-light)] p-4 z-50">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => setShowSummary(!showSummary)}
            className="flex items-center gap-2 text-[14px] text-[var(--color-secondary)]"
          >
            <svg
              className={cn("w-4 h-4 transition-transform", showSummary && "rotate-180")}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
            {showSummary ? "Hide" : "Show"} order summary
          </button>
          <span className="price-total tabular-nums">{formatCurrency(totalCents)}</span>
        </div>

        {showSummary && (
          <div className="mb-4 max-h-60 overflow-y-auto border-t border-[var(--color-surface-border-light)] pt-4">
            {orderData.items.map((item) => (
              <div key={item.id} className="flex items-center gap-3 py-2">
                <div className="relative">
                  <div className="w-12 h-12 bg-[var(--color-bg-secondary)] rounded border border-[var(--color-surface-border-light)] flex items-center justify-center overflow-hidden">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <svg
                        className="w-6 h-6 text-[var(--color-text-placeholder)]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    )}
                  </div>
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[var(--color-text-muted)] text-white text-[10px] font-medium rounded-full flex items-center justify-center">
                    {item.quantity}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-[var(--color-text-primary)] truncate">
                    {item.title}
                  </p>
                  <p className="truncate text-[12px] text-[var(--color-text-muted)] mt-0.5">
                    Quantity: {item.quantity}
                  </p>
                </div>
                <span className="text-[13px] font-medium text-[var(--color-text-primary)] tabular-nums">
                  {formatCurrency(item.line_total_cents)}
                </span>
              </div>
            ))}
            <div className="divider" />
            <div className="space-y-2 text-[14px]">
              <div className="flex justify-between">
                <span className="text-[var(--color-text-secondary)]">Subtotal</span>
                <span className="tabular-nums">{formatCurrency(subtotalCents)}</span>
              </div>
              {selectedShippingMethod && shippingCents > 0 && (
                <>
                  <div className="flex justify-between">
                    <span className="text-[var(--color-text-secondary)]">Shipping</span>
                    <span className="tabular-nums">{formatCurrency(shippingCents)}</span>
                  </div>
                  <p className="text-[12px] text-[var(--color-text-muted)] -mt-1">
                    {selectedShippingMethod.name}
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        <div className="border-t border-[var(--color-surface-border-light)] pt-4 px-4 flex items-center justify-center gap-1.5 text-xs text-[var(--color-text-muted)]">
          <span>Powered by</span>
          {resolvedTheme === "light" ? (
            <img src="/images/nile-wordmark-dark.png" alt="Nile" className="h-3.5" />
          ) : (
            <img src="/images/nile-wordmark-light.png" alt="Nile" className="h-3.5" />
          )}
        </div>
      </div>

      {/* Spacer for mobile sticky footer */}
      <div className="lg:hidden h-32" />

      <footer className="hidden lg:flex border-t border-[var(--color-surface-border-light)] p-4 z-50 items-center justify-center gap-1.5 text-xs text-[var(--color-text-muted)]">
        <span>Powered by</span>
        {resolvedTheme === "light" ? (
          <img src="/images/nile-wordmark-dark.png" alt="Nile" className="h-3.5" />
        ) : (
          <img src="/images/nile-wordmark-light.png" alt="Nile" className="h-3.5" />
        )}
      </footer>
    </div>
  );
}
